import { isNative } from './platform.js';
import { getTokens, setTokens, clearTokens } from './tokenStore.js';

const resolveApiBaseUrl = () => {
  const envUrl = (import.meta.env.VITE_API_URL || '').trim();
  if (envUrl) return envUrl;
  if (import.meta.env.DEV) return 'http://localhost:8000';
  throw new Error('VITE_API_URL não configurada para build de produção.');
};

export const API_BASE_URL = resolveApiBaseUrl();

const REQUEST_TIMEOUT_MS = 15000;

// Operações que legitimamente levam muito mais que o timeout padrão e que
// antes rodavam sem timeout nenhum:
//  - Sínodo Virtual: duas completions de 900 tokens em paralelo + uma síntese
//    de 700 (services/synod_service.py:63-75), sem streaming;
//  - exportação de dados (LGPD): serializa todo o histórico do usuário.
export const LONG_REQUEST_TIMEOUT_MS = 180000;

// AbortSignal.timeout() só existe a partir do Safari 16 / iOS 16, mas o projeto
// iOS gerado pelo Capacitor tem IPHONEOS_DEPLOYMENT_TARGET = 15.0. No WKWebView
// do iOS 15 a chamada direta lançaria "AbortSignal.timeout is not a function"
// em TODA requisição do app. Fallback manual com AbortController + setTimeout.
function _timeoutSignal(ms) {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return { signal: AbortSignal.timeout(ms), clear: () => {} };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

// No nativo o backend só devolve access_token/refresh_token/session_id no
// corpo quando a requisição carrega este header (backend/routes/authent.py:
// 375-378, :517-518). Na web isso não muda nada — a sessão continua 100%
// via cookie HttpOnly.
async function _nativeAuthHeaders(baseHeaders) {
  const headers = { ...baseHeaders, 'X-Client-Platform': 'native' };
  const tokens = await getTokens();
  if (tokens?.accessToken) headers.Authorization = `Bearer ${tokens.accessToken}`;
  return headers;
}

// Singleton: evita múltiplos refreshes simultâneos quando requisições paralelas recebem 401
let _refreshPromise = null;

// Exportado para services/chatStream.js reaproveitar o singleton de refresh
// no 401 do streaming SSE, em vez de duplicar a lógica de auth aqui.
export function refreshSession(native) {
  if (!_refreshPromise) {
    _refreshPromise = (async () => {
      if (native) {
        const tokens = await getTokens();
        const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Client-Platform': 'native' },
          credentials: 'omit',
          body: JSON.stringify({ refresh_token: tokens?.refreshToken, session_id: tokens?.sessionId }),
        });
        const data = await res.json().catch(() => null);
        // /auth/refresh ROTACIONA o refresh token (o anterior é revogado no
        // servidor) e, no modo nativo, devolve o novo par no corpo. Persistir
        // o refresh_token novo antes do retry é obrigatório — senão o próximo
        // refresh usa um token revogado e desloga o usuário.
        if (res.ok && data?.access_token) {
          await setTokens({
            accessToken: data.access_token,
            ...(data.refresh_token ? { refreshToken: data.refresh_token } : {}),
            ...(data.session_id ? { sessionId: data.session_id } : {}),
          });
        }
        return res.ok;
      }
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      return res.ok;
    })().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
}

async function request(path, { method = 'GET', headers = {}, body, token, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
  const native = isNative();
  let finalHeaders = native ? await _nativeAuthHeaders(headers) : { ...headers };
  if (token) finalHeaders.Authorization = `Bearer ${token}`;

  const timeout = _timeoutSignal(timeoutMs);
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: finalHeaders,
      body,
      credentials: native ? 'omit' : 'include',
      signal: timeout.signal,
    });
  } finally {
    timeout.clear();
  }

  // Auto-refresh em 401: tenta renovar a sessão (cookie na web, refresh_token
  // no nativo) e faz retry. Exclui rotas de auth para evitar loop infinito.
  if (res.status === 401 && !path.startsWith('/auth/')) {
    try {
      const ok = await refreshSession(native);
      if (ok) {
        const retryHeaders = native ? await _nativeAuthHeaders(headers) : finalHeaders;
        if (token) retryHeaders.Authorization = `Bearer ${token}`;
        const retryTimeout = _timeoutSignal(timeoutMs);
        let retryRes;
        try {
          retryRes = await fetch(`${API_BASE_URL}${path}`, {
            method,
            headers: retryHeaders,
            body,
            credentials: native ? 'omit' : 'include',
            signal: retryTimeout.signal,
          });
        } finally {
          retryTimeout.clear();
        }
        const data = await retryRes.json().catch(() => null);
        return { res: retryRes, data };
      }
      if (native) {
        await clearTokens();
        window.dispatchEvent(new Event('auth:expired'));
      }
    } catch {
      // refresh falhou — dispara evento global e retorna a resposta 401 original
      if (native) await clearTokens();
      window.dispatchEvent(new Event('auth:expired'));
    }
  }

  const data = await res.json().catch(() => null);
  return { res, data };
}

export function parseApiError(data, fallbackMessage) {
  if (Array.isArray(data?.detail) && data.detail.length > 0) {
    const first = data.detail[0];
    if (typeof first?.msg === 'string') return first.msg;
  }
  if (typeof data?.error?.message === 'string') return data.error.message;
  if (typeof data?.detail === 'string') return data.detail;
  return fallbackMessage;
}

// Palavras que aparecem nas mensagens de EntitlementError (P23B) — usadas
// para distinguir um 403 de "recurso bloqueado pelo plano" (que ganha um CTA
// de upgrade) de outros 403 do sistema (ex: "Acesso restrito a
// administradores"), sem precisar que cada chamador declare isso manualmente.
const _UPGRADE_HINT_WORDS = ['plano', 'planos', 'assinante', 'assinantes', 'upgrade', 'assine'];

export function classifyApiError(res, data, fallbackMessage) {
  if (!res) return { type: 'network', message: 'Falha de conexão. Verifique sua internet.' };
  if (res.status >= 500) return { type: 'server', message: 'Serviço temporariamente indisponível.' };
  const message = parseApiError(data, fallbackMessage);
  if (res.status === 403 && typeof message === 'string') {
    const normalized = message.toLowerCase();
    if (_UPGRADE_HINT_WORDS.some((word) => normalized.includes(word))) {
      return { type: 'upgrade', message };
    }
  }
  return { type: 'business', message };
}

export const apiClient = {
  request,
  parseApiError,
  login: async ({ email, password }) => {
    const result = await request('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ username: email, password }).toString(),
    });
    if (isNative() && result.res.ok && result.data?.access_token) {
      await setTokens({
        accessToken: result.data.access_token,
        refreshToken: result.data.refresh_token,
        sessionId: result.data.session_id,
      });
    }
    return result;
  },
  register: (payload) => request('/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }),
  logout: async ({ token, sessionId } = {}) => {
    let finalSessionId = sessionId;
    if (isNative() && !finalSessionId) {
      const tokens = await getTokens();
      finalSessionId = tokens?.sessionId ?? undefined;
    }
    const result = await request('/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: finalSessionId }),
      token,
    });
    if (isNative()) await clearTokens();
    return result;
  },
  getSession: ({ token } = {}) => request('/auth/session', { token }),
  confirmPasswordResetCode: ({ email, code, new_password }) => request('/auth/password-reset-confirm-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, new_password }),
  }),
  acceptTerms: (payload, { token } = {}) => request('/auth/terms/accept', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    token,
  }),
  saveOnboarding: (payload) => request('/auth/onboarding', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }),

  getAdminMetrics: ({ token } = {}) => request('/admin/metrics', { token }),
  getAdminMetricsOverview: ({ token } = {}) => request('/admin/metrics/overview', { token }),
  // Sem chamadores no frontend (doações descontinuadas); o endpoint segue existindo só p/ dados históricos.
  getAdminMetricsDonations: ({ token, from, to, group_by, status_filter } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (group_by) params.set('group_by', group_by);
    if (status_filter) params.set('status_filter', status_filter);
    const qs = params.toString();
    return request(`/admin/metrics/donations${qs ? `?${qs}` : ''}`, { token });
  },
  getAdminMetricsQueries: ({ token, from, to, group_by, user_email } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (group_by) params.set('group_by', group_by);
    if (user_email) params.set('user_email', user_email);
    const qs = params.toString();
    return request(`/admin/metrics/queries${qs ? `?${qs}` : ''}`, { token });
  },
  getAdminAuditEvents: ({ token, action, from, to, limit } = {}) => {
    const params = new URLSearchParams();
    if (action) params.set('action', action);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (limit != null) params.set('limit', String(limit));
    const qs = params.toString();
    return request(`/admin/audit/events${qs ? `?${qs}` : ''}`, { token });
  },
  getAdminUsers: ({ token, detail } = {}) => request(`/admin/users${detail ? '?detail=true' : ''}`, { token }),
  getAdminSubscriptionsSummary: ({ token } = {}) => request('/admin/subscriptions/summary', { token }),
  getAdminConversations: ({ token, user_id, user_query, from, to, conversation_query, limit } = {}) => {
    const params = new URLSearchParams();
    if (user_id) params.set('user_id', user_id);
    if (user_query) params.set('user_query', user_query);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (conversation_query) params.set('conversation_query', conversation_query);
    if (limit != null) params.set('limit', String(limit));
    const qs = params.toString();
    return request(`/admin/conversations${qs ? `?${qs}` : ''}`, { token });
  },

  // Mailing & Optin
  getAdminMailingSubscribers: ({ token } = {}) => request('/admin/mailing/subscribers', { token }),
  updateAdminMailingSubscriber: ({ token, userId, unsubscribed_marketing }) =>
    request(`/admin/mailing/subscribers/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ unsubscribed_marketing }),
      token
    }),
  sendAdminMailing: ({ token, payload }) =>
    request('/admin/mailing/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      token
    }),
  getAdminMailingCampaigns: ({ token } = {}) => request('/admin/mailing/campaigns', { token }),

  // Moderação de denúncias de conteúdo (P1-05)
  getAdminContentReports: ({ token, status = 'pending', limit = 50, offset = 0 } = {}) => {
    const params = new URLSearchParams({ status, limit: String(limit), offset: String(offset) });
    return request(`/admin/content-reports?${params.toString()}`, { token });
  },
  updateAdminContentReport: ({ token, reportId, status }) =>
    request(`/admin/content-reports/${reportId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
      token
    }),

  // Sem chamadores no frontend hoje; o endpoint /admin/usage/summary existe.
  getUsageSummary: ({ token } = {}) => request('/admin/usage/summary', { token }),
  getConversations: ({ token } = {}) => request('/chat/conversations', { token }),
  getMessages: ({ token, conversationId }) => request(`/chat/${conversationId}/messages`, { token }),
  sendMessage: ({ token, conversationId, payload }) => request(`/chat/${conversationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    token,
  }),
  renameConversation: ({ token, conversationId, title }) => request(`/chat/conversations/${conversationId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
    token,
  }),
  deleteConversation: ({ token, conversationId }) => request(`/chat/conversations/${conversationId}`, {
    method: 'DELETE',
    token,
  }),
  deleteLastMessage: ({ token, conversationId } = {}) => request(`/chat/${conversationId}/messages/last`, {
    method: 'DELETE',
    token,
  }),
};